/**
 * House model shared by server and web.
 *
 * Every home is a separate Home Assistant instance. Entity ids are only unique
 * within a home, so anything crossing a home boundary (grants, audit, WS
 * messages) carries the home id alongside the entity id.
 */
import { z } from 'zod';
/** Home slug, e.g. "woodlands", "flat". Lowercase, url-safe. */
export const HomeId = z.string().regex(/^[a-z0-9][a-z0-9-]{0,31}$/);
/** HA entity id, e.g. "light.kitchen_main_5". */
export const EntityId = z.string().regex(/^[a-z_]+\.[a-z0-9_]+$/);
export const entityKey = (home, entityId) => `${home}:${entityId}`;
export const parseEntityKey = (key) => {
    const i = key.indexOf(':');
    return { home: key.slice(0, i), entityId: key.slice(i + 1) };
};
export const domainOf = (entityId) => entityId.slice(0, entityId.indexOf('.'));
/** Optional dashboards/feature bundles, enabled per home. */
export const ModuleId = z.enum([
    'energy', // power flow, Agile, batteries, EV (Woodlands)
    'aircon', // Aircon Modulator duct schematic + tuning (Woodlands)
    'hottub', // spa chemistry + pumps (Woodlands)
    'security', // alarm panel(s), PIRs, cameras
    'door', // lock/latch/intercom
    'leak', // leak sensors
    'network', // Starlink, UniFi client switches (admin only)
    'weather',
    'occupancy', // input_select.current_occupant (flat)
    'house', // house-wide helpers (night mode, automatic lights, heating off…), curated per home
    'maintenance', // batteries, offline devices, suspect readings, to-dos (admin by default, grantable)
    'music', // Spotify (shared house account) on the Sonos speakers: view = browse, control = play
    'heating', // whole-house heating: boiler (Vaillant), Neo rooms, hot water, gas (Woodlands)
    'vacuum', // robot vacuum (Roborock): room cleans, dock, consumables, run history (Woodlands)
    'irrigation', // garden watering: ESPHome zone valves, rain, the watering automation (Woodlands)
    'bins', // bin collections (council MQTT bins): next pickup, put-out reminder + acknowledgement (Woodlands)
    'guests', // guest stays: invites, door/alarm/Wi-Fi for the stay, HA guest-stay helpers; the guest's "Your stay" page
    'site', // outdoor site plan: garden / driveway / garage with device markers + live state (Woodlands)
    'calendar', // family Google calendar ("Nat & Lauren"): view = read, control = create / edit / delete events
    'flights', // planes overhead (adsb.lol live map) + flyover log (Woodlands)
]);
/**
 * Modules holding personal data: a whole-home grant doesn't include them, only an explicit module grant
 * (or being a home admin) does. Keeps the family calendar away from lodgers with a home "view" grant.
 */
export const PRIVATE_MODULES = ['calendar'];
export const HomeSummary = z.object({
    id: HomeId,
    name: z.string(),
    timezone: z.string(),
    icon: z.string().optional(),
    online: z.boolean(),
    haVersion: z.string().optional(),
    /** Short live status line for the switcher, e.g. "Armed · 20.5°". Server-computed. */
    status: z.string().optional(),
});
export const Floor = z.object({ id: z.string(), name: z.string(), order: z.number() });
export const RoomKind = z.enum(['living', 'bedroom', 'bathroom', 'kitchen', 'circulation', 'utility', 'outdoor', 'plant', 'other']);
export const Room = z.object({
    id: z.string(),
    name: z.string(),
    floorId: z.string().nullable(),
    kind: RoomKind,
    icon: z.string().optional(),
    order: z.number(),
    /**
     * Part of another room (e.g. an ensuite inside its bedroom): shown as a tab on the parent's
     * page instead of its own home-screen tile. Same floor, one level only (see rooms.ts).
     */
    parentId: z.string().nullable().optional(),
});
export const DeviceType = z.enum([
    'thermostat', 'aircon', 'light', 'cover', 'energy', 'ev', 'hottub', 'security',
    'door', 'network', 'media', 'sensor', 'appliance', 'vehicle', 'helper', 'other',
]);
export const Device = z.object({
    id: z.string(),
    name: z.string(),
    /** Name inside its room (room prefix stripped), e.g. "Pendants" in the Kitchen. Falls back to name. */
    shortName: z.string().optional(),
    manufacturer: z.string().nullable(),
    model: z.string().nullable(),
    roomId: z.string().nullable(),
    /** Grouping key for the hierarchical config UI, e.g. "Heating", "Energy". */
    system: z.string().nullable(),
    /** Functional device type for the Settings page (server/src/ha/device-types.ts). */
    type: DeviceType.optional(),
    /** Icon key from the curated web icon set (admin override); otherwise derived from type. */
    icon: z.string().optional(),
});
/**
 * A validation warning attached to a setting by app policy, shared by the server
 * (gateway) and every client. Fires when a proposed value is above/below a number
 * or equals one of `values` (select / boolean states). `hard` warnings need a
 * typed confirmation in the UI and data._confirm === true at the gateway.
 */
export const SettingWarning = z.object({
    above: z.number().optional(),
    below: z.number().optional(),
    values: z.array(z.string()).optional(),
    text: z.string().min(1).max(400),
    hard: z.boolean().optional(),
});
/**
 * Registry metadata for one entity after app-side room mapping is applied.
 * Only entities that survive filtering (not disabled, not hidden-by-policy,
 * not owned by another home) are included.
 */
export const EntityMeta = z.object({
    id: EntityId,
    /** Full display name (context-free), e.g. "Kitchen pendants". */
    name: z.string(),
    /** Short name for use inside its device/room context, e.g. "Pendants". Falls back to name. */
    shortName: z.string().optional(),
    /** `name` is an app policy label (curated or owner rename), not derived from HA. */
    labelled: z.boolean().optional(),
    /** Where this entity belongs in the UI. 'control'/'reading' appear on room pages; 'setting'/'diagnostic' only on Settings. */
    role: z.enum(['control', 'reading', 'setting', 'diagnostic']).optional(),
    domain: z.string(),
    deviceId: z.string().nullable(),
    roomId: z.string().nullable(),
    /** Other rooms whose pages also show it (e.g. a speaker two open-plan rooms share). It stays in `roomId` everywhere else (access, music, counts). */
    alsoRoomIds: z.array(z.string()).optional(),
    platform: z.string(),
    /** HA entity_category: null = primary, 'config' = setting, 'diagnostic'. */
    category: z.enum(['config', 'diagnostic']).nullable(),
    deviceClass: z.string().nullable(),
    unit: z.string().nullable(),
    icon: z.string().nullable(),
    /** UI hints set by app policy (e.g. machine-owned helpers). */
    readOnly: z.boolean().optional(),
    readOnlyReason: z.string().optional(),
    /** Requires an extra confirmation to change (e.g. G98/G99 protection). */
    dangerous: z.boolean().optional(),
    /** Display unit normalisation, e.g. kW→W. Applied by the server before sending. */
    scale: z.number().optional(),
    /** Only sent to home admins (e.g. UniFi client block switches, occupant selector). */
    adminOnly: z.boolean().optional(),
    /** Only the owner may change it (e.g. G98/G99 grid-protection limits). Enforced by the gateway. */
    ownerOnly: z.boolean().optional(),
    /** Privilege needed (besides capability) to operate it, e.g. door latch → door_unlock. Admins are exempt. Gateway-enforced. */
    requiresPrivilege: z.enum(['door_unlock', 'intercom_open', 'alarm_arm', 'alarm_disarm']).optional(),
    /** Curated house-wide setting (module `house`): operable with `control` rather than `configure`. */
    house: z.boolean().optional(),
    /** Policy validation warnings for proposed values (see SettingWarning). */
    warnings: z.array(SettingWarning).optional(),
    /** Policy explanation shown under the setting (e.g. "Hard-capped at 95 % SOC"). */
    hint: z.string().optional(),
    /** Policy settings group, e.g. "Generator (experimental · not commissioned)". */
    group: z.string().optional(),
    /**
     * Enabled HA automations that write this setting (directly or `via` a script they run).
     * Present → read-only "Set automatically by …" unless policy readOnly:false ("Allow manual changes").
     */
    writtenBy: z.array(z.object({ id: z.string(), name: z.string(), via: z.string().optional() })).optional(),
});
/** Compact live state. Attributes are passed through as HA sends them. */
export const EntityState = z.object({
    s: z.string(), // state
    a: z.record(z.string(), z.unknown()), // attributes
    lc: z.number(), // last_changed, epoch seconds
    lu: z.number(), // last_updated, epoch seconds
});
/** Role a climate entity plays within a climate system. */
export const ClimateSourceRole = z.enum(['heat-primary', 'cool', 'heat-cool', 'mirror']);
/**
 * A climate system groups thermostats/sources serving one or more rooms.
 * Woodlands: one per room (Neo heat-primary + Daikin zone cool).
 * Flat: "Flat HVAC" (Nest master heat-cool + NSPanel mirrors + ParkAir cool) and "Bathroom".
 */
export const ClimateSystem = z.object({
    id: z.string(),
    name: z.string(),
    roomIds: z.array(z.string()),
    sources: z.array(z.object({ entityId: EntityId, role: ClimateSourceRole })),
    /** Ranked room-temperature sensors; first available wins. Falls back to the primary source's current_temperature. */
    sensors: z.array(EntityId),
    humidity: z.array(EntityId).default([]),
    boost: z.object({ deltaC: z.number(), minutes: z.number() }),
});
/** An active "I'm cold" boost. */
export const Boost = z.object({
    id: z.string(),
    systemId: z.string(),
    startedBy: z.string(),
    startedAt: z.number(),
    endsAt: z.number(),
    previousTarget: z.number(),
    boostedTarget: z.number(),
});
/** Groups on the House settings section. */
export const HouseGroup = z.enum(['lighting', 'climate', 'occupancy', 'other']);
/** One curated house-wide setting (module `house`), with a friendly label and one-line explanation. */
export const HouseSetting = z.object({
    entityId: EntityId,
    group: HouseGroup,
    label: z.string().optional(),
    description: z.string().optional(),
    /** Shown as a one-tap toggle on the home screen. */
    quick: z.boolean().optional(),
});
/**
 * App-side light group (admin-defined, never written to HA): one tile in its room that switches
 * and dims every member together (light.turn_on/turn_off with all member ids, checked per member).
 */
export const LightGroup = z.object({
    id: z.string().min(1).max(64).regex(/^[a-z0-9][a-z0-9_-]*$/),
    roomId: z.string().min(1),
    name: z.string().min(1).max(60),
    members: z.array(EntityId).min(2).max(32),
    /** Curated web icon key (e.g. "downlights"); defaults to the members' fitting. */
    icon: z.string().max(48).optional(),
});
/** Reference to a light group inside a room layout section (`group:<id>`). */
export const LIGHT_GROUP_REF = 'group:';
/**
 * Per-room light layout override (admin): named sections in order, each listing light entity ids
 * or `group:<id>` refs. Replaces the automatic fitting classifier for that room; lights it doesn't
 * list still show, in a trailing "Other lights" section.
 */
export const RoomLayout = z.object({
    roomId: z.string().min(1),
    sections: z.array(z.object({
        id: z.string().min(1).max(40).regex(/^[a-z0-9][a-z0-9_-]*$/),
        title: z.string().min(1).max(60),
        icon: z.string().max(48).optional(),
        items: z.array(z.string().min(1).max(120)).max(64),
    })).max(12),
});
export const HomeModel = z.object({
    home: HomeSummary,
    floors: z.array(Floor),
    rooms: z.array(Room),
    devices: z.record(z.string(), Device),
    entities: z.record(z.string(), EntityMeta),
    climate: z.array(ClimateSystem),
    modules: z.array(ModuleId),
    boosts: z.array(Boost),
    /** Curated house-wide settings (module `house`), in display order. */
    house: z.array(HouseSetting).optional(),
    /** App-side light groups (members present in this model only). */
    lightGroups: z.array(LightGroup).optional(),
    /** Per-room light layout overrides. */
    roomLayouts: z.array(RoomLayout).optional(),
});
//# sourceMappingURL=model.js.map